/**
 * Balanceamento como DADO, não como código (invariante 6).
 * NEVER hardcode nenhum destes números em lógica de jogo.
 *
 * Mudança aqui dispara `pnpm gamedata:diff` no CI, que mostra o diff legível
 * e roda o corpus de replays em cima (REV-17).
 */
export const balance = {
  /** Hz. Fonte única também para o cliente. Ver BUD-tick em docs/budgets/PERF.yaml. */
  tickRate: 30,
  /** ALWAYS divisor exato de tickRate, senão o intervalo de snapshot oscila. */
  snapshotRate: 15,
  interpolationBufferMs: 100,

  arena: {
    /** Raio da arena, em unidades de mundo. */
    radius: 20,
  },

  player: {
    radius: 0.5,
    startHp: 100,
    /** Velocidade máxima, unidades/s. */
    moveSpeed: 6,
    /** Aceleração até a velocidade alvo, unidades/s². */
    accel: 40,
    /** Desaceleração quando sem input, unidades/s². */
    friction: 30,
  },

  /**
   * O ímã — o verbo do jogo (`ADR-0013`, `gameplay.magnetic_rule` do PULSO).
   *
   * Tudo aqui é HIPÓTESE de feel, e feel NEVER se decide por argumento: estes
   * números existem para serem mexidos contra o aparelho, e a única razão de
   * estarem aqui em vez de na física é o invariante 6.
   *
   * O objetivo declarado do PULSO é `comportamento consistente e improvisável`,
   * NEVER eletromagnetismo realista: por isso o campo é uma aceleração constante
   * com teto, e NEVER cai com o quadrado da distância.
   */
  magnet: {
    /** Alcance do campo, em unidades de mundo. */
    range: 9,
    /**
     * Meio-ângulo do cone de auto-lock, em graus. Generoso de propósito: o alvo é
     * escolhido com o polegar em tela de celular, e `erro de mira não domina a
     * experiência` é critério de teste do PULSO, NEVER preferência.
     */
    coneDegrees: 60,
    /**
     * Força do campo. Vira aceleração dividida pela massa de quem cede — é isto
     * que faz `objeto pesado` ser um dado novo, e NEVER um caminho novo de código.
     */
    force: 30,
    /** Massa do robô. Ele é o que cede quando o alvo está preso ao cenário. */
    playerMass: 1.2,
    /** Teto da velocidade de aproximação da âncora, unidades/s. */
    pullMaxSpeed: 14,
    /**
     * Fração do impulso que sobrevive a soltar (`on_release: conserva parte do
     * impulso`). Abaixo de 1 de propósito: soltar tem que ser decisão, e NEVER
     * um jeito grátis de guardar velocidade.
     */
    releaseKeep: 0.85,
    /**
     * Arrasto do impulso do robô, unidades/s². MUITO menor que `player.friction`,
     * e é essa diferença que existe o campo separado: com a fricção de andar, o
     * impulso morreria em 0,2 s e `atravessar vãos` NEVER seria possível.
     */
    impulseDrag: 4,
    /** Distância em que o objeto solto é capturado e passa a acompanhar o robô. */
    captureDistance: 1.2,
    /** Onde o objeto capturado orbita, à frente do robô. */
    holdDistance: 1.1,
    /** Velocidade do lançamento ao soltar objeto capturado, unidades/s. */
    launchSpeed: 18,
    /** Arrasto do corpo solto, unidades/s². */
    bodyDrag: 6,
  },

  /**
   * Os corpos da sala cinza. `fixed` é a propriedade que o PULSO chama de
   * `fixação`, e é ela que decide QUEM se move: preso ao cenário puxa o robô,
   * solto vem até ele (`gameplay.target_states`).
   */
  bodies: {
    anchor: { radius: 1, mass: 0, fixed: true, capturable: false },
    light: { radius: 0.4, mass: 0.8, fixed: false, capturable: true },
    /**
     * O objeto PESADO do `first_prototype`. `gameplay.target_states` do PULSO diz o
     * que ele tem que fazer: `move lentamente ou exige campos combinados`, e
     * `mantém momentum proporcional` ao soltar.
     *
     * Massa 5 vezes a do leve, e a conta é a que decide: a aceleração do campo é
     * `magnet.force / mass`, então ele ganha 7,5 u/s² contra os 37,5 do leve — e
     * `bodyDrag` come 6 desses. Sobram 1,5 u/s² com UM campo, e 9 com DOIS. É essa
     * distância entre 1,5 e 9 que faz `cooperação` ser a resposta óbvia em vez de
     * uma sugestão do tutorial.
     *
     * `capturable: false` é a outra metade, e ela vem do PULSO também: o leve
     * `atrai e captura`, o pesado NEVER — o texto dele NEVER diz captura, diz mover.
     * Capturável, o pesado grudaria na mão e passaria a andar na velocidade do robô,
     * que é o oposto exato de `move lentamente`.
     *
     * HIPÓTESE de feel, como todo o resto de `magnet`: quem decide se 4 é pesado
     * demais é o aparelho na mão.
     */
    heavy: { radius: 0.7, mass: 4, fixed: false, capturable: false },
    /**
     * O **Núcleo Vivo** — o objetivo do jogo (`core_objective`: `localizar,
     * carregar e extrair um Núcleo Vivo antes do colapso`).
     *
     * `capturable: true` é a palavra CARREGAR virando dado: o Núcleo gruda no robô
     * e vai junto, e é isso que faz a extração ser uma viagem com um compromisso
     * na mão, e NEVER um empurrão a distância.
     *
     * Massa 2,5 vezes a do leve, e a conta é a mesma do pesado: `magnet.force /
     * mass` dá 15 u/s², e `bodyDrag` come 6 — sobram 9 com UM campo, contra 31,5
     * do leve. Solo DÁ, e é de propósito (`solo completo`, `ADR-0013`); em dupla
     * dá o dobro. O Núcleo NEVER é o objeto de `cooperação` — esse é o pesado.
     *
     * Raio 0,5: `captureDistance` é 1,2 e `holdDistance` é 1,1, então o contato
     * (0,5 do robô + 0,5 dele) tem que caber embaixo dos dois. Quem reprova se
     * alguém aumentar é `checkMagnetInvariants`, e NEVER a revisão.
     *
     * HIPÓTESE de feel, como todo o resto: quem decide se carregar o Núcleo é bom
     * é o aparelho na mão.
     */
    core: { radius: 0.5, mass: 2, fixed: false, capturable: true },
  },

  /**
   * A sala cinza do `first_prototype`: âncora, objeto leve e objeto pesado.
   *
   * Chamava-se `soloRoom` enquanto só a partida local a carregava. A sala em REDE
   * passou a carregar a MESMA (`ADR-0017`), e um nome que diz "solo" para conteúdo
   * que os dois caminhos usam é um nome que mente.
   * O Núcleo e a saída entraram em `objective`, abaixo. Condutor e PERIGO ainda
   * NEVER existem: o perigo é do `build` do `first_prototype` e espera decisão de
   * design (que forma ele tem); o condutor NEVER é do protótipo.
   *
   * Fica em volta da ORIGEM de propósito. Amarrar a posição ao spawn calculado
   * pelo sim faria a sala quebrar em silêncio no dia em que `room.maxPlayers` ou
   * `arena.radius` mudassem — e o spawn NEVER é contrato de conteúdo.
   *
   * Longe UM DO OUTRO também de propósito: com os dois colados, parado no meio da
   * sala o auto-lock decide no empate de distância, e o jogador NEVER teria como
   * prever qual dos dois o campo vai pegar. Separados, quem decide é a mira.
   */
  protoRoom: {
    bodies: [
      { kind: 'anchor', x: 0, z: -7 },
      { kind: 'light', x: 0, z: 7 },
      // O pesado no TERCEIRO braço, e NEVER ao lado de um dos outros: com quatro
      // assentos em (±12, 0) e (0, ±12), cada objeto fica na frente de um assento
      // diferente. O objeto de `cooperação` longe do de `coletar` é o que faz o
      // grupo ter que se mover para usá-lo, em vez de os três caberem num gesto só.
      { kind: 'heavy', x: -7, z: 0 },
    ],
    /**
     * O objetivo: o Núcleo e a saída. Fica SEPARADO de `bodies` porque a fatia
     * inteira nasce atrás da flag `objective` (invariante 7), e uma flag que
     * NEVER consegue tirar a feature do mundo NEVER é uma flag — com o Núcleo
     * dentro de `bodies` só restaria filtrar por tipo na hora de montar a sala.
     */
    objective: {
      /**
       * O Núcleo no QUARTO braço, o único que ainda estava vazio: com quatro
       * assentos em (±12, 0) e (0, ±12), cada objeto da sala fica na frente de um
       * assento diferente, e o Núcleo NEVER nasce no colo de ninguém.
       */
      core: { x: 7, z: 0 },
      /**
       * A saída na DIAGONAL, e longe: do Núcleo até aqui são ~19,6 unidades, mais
       * do que o diâmetro do miolo onde estão os corpos. É o que transforma
       * extrair em VIAGEM — com a saída ao lado do Núcleo, o objetivo do jogo
       * inteiro caberia num gesto, e o `first_prototype` deixaria de perguntar o
       * que veio perguntar.
       *
       * Diagonal de propósito: nos quatro braços já tem corpo e assento, e uma
       * saída em cima de um deles faria o jogador atravessar a sala mirando um
       * objeto que NEVER é o dele.
       *
       * Raio 2,5 — 5 vezes o robô. Alvo grande porque acertar a saída NEVER é o
       * desafio: o desafio é chegar nela com o Núcleo.
       */
      exit: { x: -10, z: -10, radius: 2.5 },
    },
    /**
     * O PERIGO do `first_prototype`: uma zona no chão que EMPURRA.
     *
     * NO CENTRO, e é o desenho inteiro da decisão: o caminho reto do Núcleo (7,0)
     * até a saída (-10,-10) passa a 3,55 unidades da origem, ou seja, DENTRO de um
     * raio 4. Contornar pela borda é seguro e longo; atravessar é curto e caro.
     * É a pergunta `vale o risco?` do laço do PULSO, desenhada no chão — e ela
     * NEVER existe se a zona ficar num canto onde ninguém precisa passar.
     *
     * Fica longe dos quatro corpos (todos a 7 da origem) e dos quatro assentos
     * (a 12): ninguém NASCE em perigo, e nada começa a partida sendo varrido.
     * Quem reprova se isso deixar de valer é `checkHazardInvariants`.
     *
     * `push` é aceleração, `maxSpeed` é o teto da velocidade que ela dá — e é o
     * TETO que decide o design, e NEVER a aceleração: menor que os 6 de
     * `moveSpeed`, o robô atravessa andando, chega do outro lado devagar e fora
     * de rumo, e NEVER consegue ficar parado lá dentro. Teto acima de `moveSpeed`
     * viraria PAREDE, e parede NEVER é decisão de risco.
     *
     * ⚠️ Era 4, e 4 tinha um PENHASCO. Direção de movimento é lida por tick: tick
     * sem input do jogador é `move = 0`, e tick sem input é o caso NORMAL — CPU
     * ocupada e rede móvel tiram pacotes o tempo todo. MEDIDO no sim, 3 s andando
     * contra a zona a partir de 3,5 de distância do centro:
     *
     *   teto 4:  100% dos ticks com input → +14,2   40% → **−2,2**
     *   teto 3:  100% → +16,3             40% → +8,6
     *
     * Com teto 4 o jogador ANDA PARA TRÁS quando a mão dele engasga: a zona vira
     * parede exatamente para quem está com a rede pior, e NEVER para quem joga no
     * cabo. Foi o E2E que achou — passava sozinho e travava com a suíte inteira
     * disputando CPU, que é o retrato do celular de verdade.
     *
     * HIPÓTESE de feel, como todo o resto: quem decide se 3 é assustador o
     * bastante é o aparelho na mão.
     */
    hazard: { x: 0, z: 0, radius: 4, push: 25, maxSpeed: 3 },
  },

  /**
   * **Fase 01 — a primeira sala DESENHADA.**
   *
   * ⚠️ HIPÓTESE, e NEVER decisão: o PULSO decide o verbo e o objetivo, e NEVER
   * decide fase — não existe seção de nível na fonte de design. Este percurso é
   * proposta de engenharia esperando olho humano, e nasce atrás da flag
   * `level01`, DESLIGADA. O `protoRoom` continua sendo a sala que vai ao ar.
   *
   * A diferença entre uma fase e a sala do protótipo NEVER é a quantidade de
   * objetos: é haver PERCURSO. No `protoRoom` tudo está a 7 da origem, um objeto
   * por braço, porque ele existia para provar que cada peça funciona. Aqui as
   * peças já funcionam, e o que se prova é outra coisa: `atravessar, manipular,
   * improvisar, resgatar e extrair` (`quick_context.elevator_pitch`).
   *
   * ```
   *                        · pesado (5,13)     z ^
   *          Núcleo (12,1 · 12,1)                |
   *        âncora ·      ○ H1  r3                +--> x
   *      (-5,7 · 5,7)      ╲
   *                     ◯ H2 r4,5   (origem)
   *                       ╲      · âncora (5,7 · -5,7)
   *                  ○ H3  r3
   *   SAÍDA (-12,1 · -12,1)
   *          leve (-5,-13) ·
   * ```
   *
   * **O eixo.** Núcleo e saída nas pontas da MESMA diagonal, a 17,1 da origem
   * cada um: 34 unidades de viagem, contra as 19,6 do protótipo. Extrair vira
   * travessia da sala inteira, e é o que dá tempo para a decisão acontecer mais
   * de uma vez. Na diagonal de propósito: os quatro assentos ficam nos eixos, e
   * um eixo de percurso em cima deles faria alguém nascer em cima da rota.
   *
   * **A espinha de perigo.** As três zonas ficam EM CIMA da reta que liga o
   * Núcleo à saída. Ir reto é atravessar as três; contornar é seguro e custa
   * caminho. É a pergunta `vale o risco?` repetida três vezes em vez de uma, e
   * repetida é o que a torna ESTRATÉGIA em vez de sorte: dá para pagar num
   * trecho e desviar no outro.
   *
   * NEVER é parede, e o teste reprova se virar: entre H1 e H2 sobram 3 unidades
   * de folga, e o robô com o Núcleo na mão ocupa 2. A primeira tentativa deste
   * desenho tinha as zonas se SOBREPONDO em 0,44 — passava no schema, passava
   * nas invariantes, e teria ido para a tela como uma parede diagonal.
   *
   * **As âncoras fora da espinha.** A 8 unidades da rota reta, uma de cada lado.
   * É o que responde `jogador entende por que às vezes ele se move e às vezes o
   * objeto se move?` (`first_prototype.questions`): a âncora é FIXA, então puxá-la
   * puxa VOCÊ — e ela está exatamente onde quem escolheu contornar precisa de
   * velocidade. Contornar deixa de ser o castigo de quem teve medo e vira a rota
   * de quem sabe usar o verbo.
   *
   * **O pesado do lado do Núcleo, o leve do lado da saída.** `cooperação` é o
   * papel do pesado, e é perto do Núcleo que o grupo tem motivo para se juntar.
   * O leve fica no trecho final, onde serve de projétil ou de peso para
   * improvisar — `improvisar` é palavra do pitch, e improviso precisa de
   * matéria-prima ao alcance. Os dois longe dos assentos, como no `protoRoom`.
   *
   * Todos os números aqui são HIPÓTESE de feel: quem decide se a espinha é
   * assustadora ou irritante é o aparelho na mão, e NEVER esta explicação.
   */
  level01: {
    bodies: [
      { kind: 'anchor', x: -5.66, z: 5.66 },
      { kind: 'anchor', x: 5.66, z: -5.66 },
      { kind: 'heavy', x: 5, z: 13 },
      { kind: 'light', x: -5, z: -13 },
    ],
    objective: {
      core: { x: 12.1, z: 12.1 },
      /** Raio 2,5, o mesmo do protótipo: acertar a saída NEVER é o desafio. */
      exit: { x: -12.1, z: -12.1, radius: 2.5 },
    },
    /**
     * `push` e `maxSpeed` são os MESMOS do `protoRoom`, e de propósito: o teto de
     * 3 foi medido contra rede ruim (`DIS-52`), e fase NEVER é lugar de
     * redescobrir número de física. O que muda de sala para sala é ONDE a zona
     * está, e NEVER o que ela faz.
     *
     * A do meio é MAIOR (4,5 contra 3) porque está na origem, onde o espaço para
     * contornar é maior: zona do mesmo tamanho ali seria a mais fácil das três de
     * evitar, e a do meio é justamente a que precisa doer.
     */
    hazards: [
      { x: 7.42, z: 7.42, radius: 3, push: 25, maxSpeed: 3 },
      { x: 0, z: 0, radius: 4.5, push: 25, maxSpeed: 3 },
      { x: -7.42, z: -7.42, radius: 3, push: 25, maxSpeed: 3 },
    ],
  },

  room: {
    /**
     * Teto de assentos. O jogo é COOPERATIVO de 2 a 4 (`ADR-0013`), e o PULSO põe
     * `mais de quatro jogadores` em `out_of_scope`.
     * NEVER aumente sem ADR: `spawnPosition` distribui no círculo por este valor,
     * então mexer aqui muda a posição inicial de todo mundo e invalida o corpus
     * de replay — é mudança de `SIM_VERSION`, e NEVER ajuste de configuração.
     */
    maxPlayers: 4,
    /**
     * Duração da partida em ticks. 600 s a 30 Hz.
     *
     * Era 180 s, e 180 s NEVER podia responder a pergunta que este protótipo
     * existe para responder: o `first_prototype` do PULSO pergunta se o jogo
     * "continua interessante por pelo menos DEZ minutos sem prêmio externo".
     * Uma partida de três minutos encerra a sessão antes da pergunta começar.
     *
     * Bate também com `session_duration_target` (8 a 10 min) e com o laço de
     * 8–10 min de `experience_loops`. NEVER é número solto: é o teto do laço
     * inteiro `entrar → achar o Núcleo → decidir risco → fugir → extrair`.
     *
     * NEVER mexe em `SIM_VERSION`: quem decide o fim é a SALA (`room.isOver`), e
     * NEVER o `step` — o corpus de replay continua válido.
     */
    matchDurationTicks: 18_000,
    /**
     * HUMAN-REVIEW: teto anti-cheat. Este número foi afrouxado a partir de medida
     * real e merece leitura sua antes de virar definitivo.
     *
     * Era 45, e 45 EXPULSAVA jogador honesto. MEDIDO em dois aparelhos contra stg
     * (celular e desktop): o cliente envia a **31 Hz**, com pior janela de 1 s de
     * **32 mensagens** — e mesmo assim levava `rateLimited`.
     *
     * A causa é que o teto conta por CHEGADA, e NEVER por envio. Quando a rede
     * engasga meio segundo e entrega o acumulado de uma vez, aquela janela recebe
     * o atrasado somado ao corrente. Com envio a 31 e teto em 45, a folga era de
     * 14 mensagens — menos de meio segundo de rede ruim. Em celular isso NEVER é
     * anomalia, é terça-feira: o teto estava medindo a operadora, e NEVER o
     * jogador.
     *
     * 120 dá ~3 s de acúmulo de rede antes de acusar, e continua barrando um
     * flooder de verdade por três ordens de grandeza.
     *
     * ALWAYS lembre que este NEVER é a defesa contra acelerar o jogo: quem
     * protege disso é `tickTooFarAhead`, que compara o tick DECLARADO com o do
     * servidor e NEVER depende de quando o pacote chegou. Este teto protege o
     * SERVIDOR de trabalho excessivo, e é só isso que ele deve fazer.
     */
    maxInputsPerSecond: 120,
    /**
     * Ticks que uma sala vazia sobrevive antes de ser destruída. 10 s a 30 Hz.
     * É carência de RECONEXÃO: em rede móvel o socket cai por alguns segundos, e
     * matar a sala na hora jogaria fora a partida de quem só passou por um túnel.
     */
    emptyRoomGraceTicks: 300,
    /**
     * Tentativas SEGUIDAS de avançar estourando antes de a sala ser destruída.
     * Em regime é uma por tick, então isto é 1 s a 30 Hz.
     *
     * Sala que lança exceção no tick NEVER envelhece: o contador de vazia e o
     * próprio tick são as últimas linhas de `advance`, então uma sala quebrada
     * fica imortal, queima CPU a 30 Hz e ocupa uma vaga de `maxRooms` para sempre.
     *
     * NEVER é 1: `broadcastSnapshot` escreve em socket, e socket morrendo no meio
     * do tick é falha passageira. Um segundo de insistência separa o transitório
     * do que está de fato quebrado.
     */
    faultyTicksBeforeClose: 30,
  },
} as const;

export type Balance = typeof balance;
